import axios from "@/config/axios";

const initialState = () => ({
  dashboard: [],
  batchResults: {},
});

const state = initialState();

const getters = {
  getDashboard: (state) => state.dashboard,
  getBatchResults: (state) => state.batchResults,
};

const mutations = {
  setDashboard(state, data) {
    state.dashboard = data;
  },
  setBatchResults(state, data) {
    state.batchResults = data;
  },
  reset: (state) => {
    const initial = initialState();
    Object.keys(initial).forEach((key) => {
      state[key] = initial[key];
    });
  },
};

const actions = {
  fetchDashboard: async ({ commit }) => {
    try {
      const response = await axios.get("/instrument-queue/dashboard");
      commit("setDashboard", response.data?.data || []);
    } catch {
      commit("setDashboard", []);
    }
  },


  fetchInstrumentResults: async ({ commit }, { instrumentId, params }) => {
    try {
      const response = await axios.get(
        `/instrument-queue/instruments/${instrumentId}/results`,
        { params }
      );
      commit("setBatchResults", response.data || {});
    } catch {
      commit("setBatchResults", {});
    }
  },

  bulkReviewInstrument: async (_, { instrumentId, resultCategory }) => {
    const response = await axios.post(
      `/instrument-queue/instruments/${instrumentId}/review`,
      { result_category: resultCategory }
    );
    return response.data;
  },

  // Accept all results inside a case + mark case ACCEPTED
  reviewCase: async (_, { caseDocId }) => {
    const response = await axios.patch(
      `/instrument-queue/cases/${caseDocId}/review`,
      { action: "ACCEPTED" }
    );
    return response.data;
  },

  // Accept or Hold a single embedded test result
  reviewSingleResult: async (_, { caseDocId, resultId, action }) => {
    const response = await axios.patch(
      `/instrument-queue/cases/${caseDocId}/results/${resultId}/review`,
      { action }
    );
    return response.data;
  },

  // Save report comment to the linked blood_results document
  updateCaseComment: async (_, { caseDocId, comment }) => {
    const response = await axios.patch(
      `/instrument-queue/cases/${caseDocId}/comment`,
      { comment }
    );
    return response.data;
  },

  // Save a per-test comment on the queue result + linked blood_results result
  updateResultComment: async (_, { caseDocId, resultId, comment }) => {
    const response = await axios.patch(
      `/instrument-queue/cases/${caseDocId}/results/${resultId}/comment`,
      { comment }
    );
    return response.data;
  },

  // Fetch pending tests (JSON) for the current lab, optionally filtered by
  // test name, ordering facility (hospital) and collection-date range.
  fetchPendingTests: async (_, params = {}) => {
    const response = await axios.get("/instrument-queue/pending-tests", { params });
    return response.data?.data || [];
  },

  // Fetch distinct facilities + test names to populate the pending-tests filters
  fetchPendingTestsFilters: async () => {
    const response = await axios.get("/instrument-queue/pending-tests/filters");
    return response.data?.data || { facilities: [], tests: [] };
  },

  // Fetch critical tests from the blood interface service (separate base URL + token).
  // Supports search_string, hospital, fromDate, toDate, is_critical, page, limit.
  fetchCriticalTests: async (_, params = {}) => {
    const response = await axios.get(
      `${process.env.VUE_APP_BLOOD_INTERFACE}/critical-values`,
      {
        headers: {
          Authorization: `${process.env.VUE_APP_BLOOD_INTERFACE_ACCESS_TOKEN}`,
        },
        params,
      }
    );
    return response.data || { data: [] };
  },

  // Search all blood results (JSON) from the blood interface service.
  // Supports test, hospital, from_date, to_date, page, limit. Returns the
  // paginated envelope ({ data, total, ... }). The ls-lab header (added by the
  // axios interceptor) scopes results to the current lab.
  fetchBloodResultsSearch: async (_, params = {}) => {
    const response = await axios.get(
      `${process.env.VUE_APP_BLOOD_INTERFACE}/blood-results/search`,
      {
        headers: {
          Authorization: `${process.env.VUE_APP_BLOOD_INTERFACE_ACCESS_TOKEN}`,
        },
        params,
      }
    );
    return response.data || { data: [], total: 0 };
  },

  // Fetch a patient's previous (prior completed) blood results for a case.
  fetchPreviousPatientResults: async (_, caseId) => {
    const response = await axios.get(
      `${process.env.VUE_APP_BLOOD_INTERFACE}/blood-results/previous-patient-results/${caseId}`,
      {
        headers: {
          Authorization: `${process.env.VUE_APP_BLOOD_INTERFACE_ACCESS_TOKEN}`,
        },
      }
    );
    return response.data?.data || [];
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
